import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GoogleReviewsLive from "../components/site/GoogleReviewsLive";
import { motion } from "framer-motion";
import {
  Eye,
  ScanEye,
  Activity,
  Droplets,
  Waves,
  Syringe,
  Stethoscope,
  ShieldCheck,
  ArrowRight,
  Star,
  Quote,
  MessageCircle,
  ArrowUpRight,
  CheckCircle2,
  Sparkles,
  GraduationCap,
  Check,
} from "lucide-react";
import SectionHeader from "../components/site/SectionHeader";
import SafeImage from "../components/site/SafeImage";
import NewspaperFeature from "../components/site/NewspaperFeature";
import { PRESS_MENTIONS } from "../data/press";
import { BOOK_SQUINT } from "../lib/books";
import { CLINIC, waLink } from "../lib/clinic";
import { CLINIC_PHOTOS, CLINIC_GALLERY } from "../lib/clinicPhotos";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { graph, REF, AUTHOR_REF, SITE } from "../lib/schema";
import api from "../lib/api";

const iconMap = {
  eye: Eye,
  "scan-eye": ScanEye,
  activity: Activity,
  droplets: Droplets,
  waves: Waves,
  syringe: Syringe,
  stethoscope: Stethoscope,
  "shield-check": ShieldCheck,
};

const stats = [
  { value: "12+", label: "Years Clinical Experience" },
  { value: "10,000+", label: "Cataract Surgeries" },
  { value: "150+", label: "Corneal Transplants" },
  { value: "GNEC · MAMC", label: "New Delhi Trained" },
];

const Home = () => {
  usePageMeta({
    title: "Dr. Shantanu Kumar Gupta | Eye Specialist Varanasi",
    path: "/",
    description: "Cataract, cornea & medical retina specialist in Varanasi. MBBS, MS (MAMC), DNB, FICO. Book on WhatsApp. Cashless insurance & Ayushman Bharat accepted.",
  });
  const [expertise, setExpertise] = useState([]);
  const [featuredBlog, setFeaturedBlog] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  // Publications stats — count + summed citations, PubMed-indexed count, and
  // the raw list (used to emit `subjectOf: [ScholarlyArticle[]]` under the
  // Physician entity so Google's Knowledge Graph joins Dr. Shantanu ↔ his
  // papers on the homepage itself).
  const [pubStats, setPubStats] = useState({ total: 0, citations: 0, pubmed: 0, items: [] });

  useEffect(() => {
    api.get("/expertise").then((r) => setExpertise(r.data)).catch(() => {});
    api.get("/blog", { params: { featured: true } }).then((r) => setFeaturedBlog(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api
      .get("/media")
      .then((r) => {
        const pubs = (r.data || []).filter((x) => x.type === "publication");
        const citations = pubs.reduce((sum, p) => sum + (typeof p.citations === "number" ? p.citations : 0), 0);
        const pubmed = pubs.filter((p) => !!p.pmid).length;
        setPubStats({ total: pubs.length, citations, pubmed, items: pubs });
      })
      .catch(() => {});
  }, []);

  // Emit a supplementary JSON-LD graph that extends the Physician entity
  // (already declared with @id=#physician in /public/index.html) with
  // `subjectOf: ScholarlyArticle[]`. Google's algorithm merges nodes with the
  // same `@id` across all JSON-LD blocks on a page, so this augments the
  // Physician's Knowledge Graph card with authored papers, PMID identifiers
  // and total citation count — without duplicating the Physician body.
  usePageJsonLd(
    pubStats.items.length > 0
      ? graph([
          // Publication list — each paper as a full ScholarlyArticle so it can
          // stand on its own in the knowledge graph and be surfaced in
          // Google Scholar / rich results independently of the Physician card.
          ...pubStats.items.map((p) => ({
            "@type": "ScholarlyArticle",
            "@id": p.doi
              ? `https://doi.org/${p.doi}`
              : p.pmid
              ? `https://pubmed.ncbi.nlm.nih.gov/${p.pmid}/`
              : p.link,
            headline: p.title,
            name: p.title,
            datePublished: p.date,
            url: p.link,
            isPartOf: { "@type": "Periodical", name: p.outlet },
            author: AUTHOR_REF,
            ...(p.pmid ? { sameAs: [`https://pubmed.ncbi.nlm.nih.gov/${p.pmid}/`] } : {}),
            ...(p.doi ? { identifier: [{ "@type": "PropertyValue", propertyID: "DOI", value: p.doi }] } : {}),
            ...(typeof p.citations === "number" && p.citations > 0
              ? {
                  interactionStatistic: {
                    "@type": "InteractionCounter",
                    interactionType: "https://schema.org/CommentAction",
                    userInteractionCount: p.citations,
                    description: "Semantic Scholar citation count",
                  },
                }
              : {}),
          })),
          // Physician node — same @id as the root Physician declared in
          // index.html; Google merges these. `subjectOf` links to the papers.
          {
            "@type": ["Physician", "Person"],
            "@id": REF.physician,
            url: `${SITE}/about`,
            subjectOf: pubStats.items.map((p) => ({
              "@id": p.doi
                ? `https://doi.org/${p.doi}`
                : p.pmid
                ? `https://pubmed.ncbi.nlm.nih.gov/${p.pmid}/`
                : p.link,
            })),
            knowsAbout: [
              "Cataract Surgery",
              "Cornea",
              "Medical Retina",
              "Diabetic Retinopathy",
              "Paediatric Ophthalmology",
              "Intraocular Lens (IOL) Implantation",
              "Endophthalmitis prophylaxis",
              "Optical Biometry",
            ],
            alumniOf: [
              { "@type": "CollegeOrUniversity", name: "University College of Medical Sciences, University of Delhi", sameAs: "https://ucms.ac.in" },
              { "@type": "CollegeOrUniversity", name: "Maulana Azad Medical College, New Delhi", sameAs: "https://mamc.ac.in" },
              { "@type": "CollegeOrUniversity", name: "Guru Nanak Eye Centre, MAMC, New Delhi" },
            ],
            memberOf: [
              { "@type": "MedicalOrganization", name: "All India Ophthalmological Society (AIOS)", sameAs: "https://aios.org" },
              { "@type": "MedicalOrganization", name: "American Academy of Ophthalmology (AAO)", sameAs: "https://www.aao.org" },
              { "@type": "MedicalOrganization", name: "International Council of Ophthalmology (ICO)", sameAs: "https://icoph.org" },
            ],
            sameAs: [
              "https://www.google.com/maps?cid=1796062888296972207",
              "https://www.shantanunetralaya.com",
              "https://www.eyedocmanishamishra.com",
              "https://www.researchgate.net/profile/Shantanu-Gupta-4",
              "https://www.youtube.com/@SHANTANU-NETRALAYA",
              "https://www.instagram.com/shantanunetralaya/",
              "https://www.facebook.com/shantanunetralaya/",
            ],
          },
        ])
      : null
  );

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 -right-24 w-[500px] h-[500px] rounded-full bg-[hsl(var(--primary))]/8 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-[500px] h-[500px] rounded-full bg-[hsl(var(--accent))]/12 blur-3xl" />
        </div>

        <div className="container-page pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="overline mb-4" data-testid="hero-overline">Premium Eye Care · Varanasi</div>
            <div
              className="inline-flex items-center gap-2 mb-7 text-[10.5px] uppercase tracking-[0.24em] text-[hsl(var(--foreground))]/70 border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/[0.06] rounded-full px-3.5 py-1.5"
              data-testid="hero-geo-pill"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))]" aria-hidden="true" />
              Varanasi · Kashi · Purvanchal
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-balance text-[hsl(var(--foreground))]">
              Clarity of vision.
              <br />
              <span className="italic text-[hsl(var(--primary))]">Precision of care.</span>
            </h1>
            <p className="mt-7 text-base sm:text-lg text-[hsl(var(--muted-foreground))] max-w-xl leading-relaxed">
              I'm <span className="font-medium text-[hsl(var(--foreground))]">Dr. Shantanu Kumar Gupta</span> —
              MBBS, MS (MAMC), DNB, FICO (Merit). Cataract, cornea and medical retina specialist trained at
              Guru Nanak Eye Centre, New Delhi. I consult at Shantanu Netralaya Eye Hospital in Varanasi.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a
                href={waLink("Hi Dr. Shantanu, I'd like to book an appointment at Shantanu Netralaya.")}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="btn-primary"
                data-testid="hero-book-whatsapp-btn"
              >
                <MessageCircle size={16} /> Book on WhatsApp
              </a>
              <Link to="/expertise" className="btn-ghost" data-testid="hero-expertise-btn">
                View Expertise <ArrowRight size={14} />
              </Link>
            </div>

            <Link
              to="/insurance"
              data-testid="hero-insurance-badge"
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/15 transition"
            >
              <ShieldCheck size={14} strokeWidth={1.75} /> Insurance & Ayushman Bharat accepted
            </Link>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6" data-testid="hero-stats">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-serif text-3xl text-[hsl(var(--primary))]">{s.value}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]">{s.label}</div>
                </div>
              ))}
            </div>

            <a
              href={CLINIC.googleProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-google-verified"
              className="mt-6 inline-flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full border border-border/70 bg-white/60 backdrop-blur hover:border-[hsl(var(--primary))]/50 transition group"
              title="Verified 4.8★ rating on Google Business Profile — click to read patient reviews"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--foreground))]/80 group-hover:text-[hsl(var(--primary))]">
                <span className="font-semibold text-[hsl(var(--foreground))]">{CLINIC.googleRating}★</span>
                <span className="text-[hsl(var(--muted-foreground))]">·</span>
                Verified on Google
                <ArrowUpRight size={12} strokeWidth={2} className="ml-0.5 opacity-60 group-hover:opacity-100" />
              </span>
            </a>

            {/* Publications credibility pill — links to /publications. Renders
                only when we have at least one publication so the hero stays
                clean if the DB hasn't been enriched yet. */}
            {pubStats.total > 0 && (
              <Link
                to="/publications"
                data-testid="hero-publications-badge"
                className="mt-3 ml-2 inline-flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full border border-border/70 bg-white/60 backdrop-blur hover:border-[hsl(var(--primary))]/50 transition group"
                title="Peer-reviewed publications indexed on PubMed & ResearchGate"
              >
                <span className="w-[18px] h-[18px] rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center text-[hsl(var(--primary))]">
                  <GraduationCap size={11} strokeWidth={2} />
                </span>
                <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--foreground))]/80 group-hover:text-[hsl(var(--primary))]">
                  <span className="font-semibold text-[hsl(var(--foreground))]">{pubStats.total}</span>
                  Peer-reviewed
                  {pubStats.citations > 0 && (
                    <>
                      <span className="text-[hsl(var(--muted-foreground))]">·</span>
                      <span className="font-semibold text-[hsl(var(--foreground))]">{pubStats.citations}</span>
                      Citations
                    </>
                  )}
                  <ArrowUpRight size={12} strokeWidth={2} className="ml-0.5 opacity-60 group-hover:opacity-100" />
                </span>
              </Link>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-tr from-[hsl(var(--primary))]/12 via-transparent to-[hsl(var(--accent))]/20 blur-2xl -z-10" />
            <div className="relative overflow-hidden rounded-[2rem] border border-border/70 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.25)]">
              <SafeImage
                src={CLINIC.doctorPortrait}
                alt="Dr. Shantanu Kumar Gupta — Cataract, Cornea & Medical Retina Specialist consulting at Shantanu Netralaya Eye Hospital, Durgakund, Varanasi"
                label="Dr Shantanu"
                loading="eager"
                className="w-full h-[520px] sm:h-[620px]"
                data-testid="hero-doctor-portrait"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 backdrop-blur-md bg-white/70 dark:bg-black/50 border-t border-white/40 dark:border-white/10">
                <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--accent))]">Consultant</div>
                <div className="font-serif text-xl text-[hsl(var(--foreground))]">Dr. Shantanu Kumar Gupta</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">MBBS · MS (MAMC) · DNB · FICO (Merit)</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-border bg-[hsl(var(--secondary))]/60 py-8" data-testid="trust-strip">
        <div className="container-page flex flex-wrap items-center justify-around gap-6 text-sm text-[hsl(var(--muted-foreground))]">
          {["AIOS Member", "Vitreo-Retinal Society of India", "10,000+ Surgeries", "MICS Phaco", "OCT & OCT-A", "Premium IOLs"].map((t) => (
            <div key={t} className="flex items-center gap-2">
              <CheckCircle2 size={16} strokeWidth={1.5} className="text-[hsl(var(--accent))]" />
              <span className="tracking-wide">{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* IOL SELECTOR SPOTLIGHT — prominent, high-in-the-fold callout for
          the interactive lens-selection tool.  This is our strongest
          conversion-intent lead magnet: patients researching cataract
          surgery cost / IOL options land here from search and are one
          click away from a personalised recommendation. */}
      <section
        className="relative py-14 sm:py-16 overflow-hidden bg-gradient-to-br from-[hsl(var(--primary))]/8 via-background to-[hsl(var(--accent))]/8 border-b border-border"
        data-testid="home-iol-spotlight"
      >
        <div className="container-page">
          <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center rounded-3xl border border-[hsl(var(--primary))]/25 bg-background/70 backdrop-blur p-8 md:p-10 shadow-sm">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] font-medium">
                <Sparkles size={12} /> Featured tool · free · 3-5 minutes
              </div>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-balance">
                Not sure which cataract lens is right for you?
              </h2>
              <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed">
                Try our free <strong>IOL Selection Suite</strong> — the only interactive lens selector in
                Purvanchal. Answer a few lifestyle questions and get a personalised recommendation
                (Monofocal · Toric · EDOF · Trifocal) with our clinic's live lens inventory, reviewed by{" "}
                <strong>Dr. Shantanu Kumar Gupta, MBBS · MS (MAMC) · DNB · FICO (Merit)</strong>. Print or
                share your report with family.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/iol-selector"
                  className="btn-primary text-xs inline-flex items-center gap-2"
                  data-testid="home-iol-cta-primary"
                >
                  Try the IOL Selector <ArrowRight size={13} />
                </Link>
                <Link
                  to="/cataract-surgery-cost-in-varanasi"
                  className="btn-outline text-xs inline-flex items-center gap-2"
                >
                  See transparent pricing
                </Link>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={11} className="text-[hsl(var(--accent))]" /> Personalised</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={11} className="text-[hsl(var(--accent))]" /> Multilingual (EN · हिन्दी · मराठी)</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={11} className="text-[hsl(var(--accent))]" /> Printable PDF report</span>
              </div>
            </div>
            <div className="hidden lg:flex items-center justify-center min-w-[220px]">
              <div className="text-center p-6 rounded-2xl border border-[hsl(var(--primary))]/20 bg-[hsl(var(--primary))]/6">
                <div className="text-5xl font-serif text-[hsl(var(--primary))]">6</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">Lens categories</div>
                <div className="mt-4 text-5xl font-serif text-[hsl(var(--primary))]">₹18k+</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">Starting price</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE GOOGLE REVIEWS — pulled from Places API (New) with 6h cache */}
      <GoogleReviewsLive variant="grid" limit={6} />

      {/* PUBLISHED AUTHORS — book by Dr. Manisha Mishra (Author) & Dr. Shantanu
          Kumar Gupta (Editor). Strong E-E-A-T signal + patient-facing
          authority anchor for squint / paediatric ophthalmology queries. */}
      <section className="py-16 border-y border-border bg-[hsl(var(--secondary))]/40" data-testid="home-book-band">
        <div className="container-page">
          <div className="grid lg:grid-cols-[minmax(0,0.5fr)_minmax(0,1fr)] gap-10 items-center max-w-5xl mx-auto">
            <Link
              to={`/book/${BOOK_SQUINT.slug}`}
              className="block relative mx-auto lg:mx-0 max-w-[220px] group"
              data-testid="home-book-cover-link"
            >
              <div className="absolute -inset-4 bg-gradient-to-br from-[hsl(var(--primary))]/15 via-transparent to-[hsl(var(--accent))]/15 rounded-3xl blur-xl -z-10" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-2xl border border-border shadow-[0_25px_60px_-25px_rgba(0,0,0,0.35)] group-hover:shadow-[0_35px_80px_-25px_rgba(0,0,0,0.45)] transition-shadow aspect-[2/3]">
                <SafeImage
                  src={BOOK_SQUINT.coverImageThumb}
                  alt={`${BOOK_SQUINT.fullTitle} — book cover`}
                  label={BOOK_SQUINT.title}
                  className="w-full h-full"
                  data-testid="home-book-cover"
                />
              </div>
            </Link>
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--accent))]">
                Now on Amazon · Published Authors
              </div>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-[42px] leading-[1.1] text-balance">
                {BOOK_SQUINT.title}: <span className="text-[hsl(var(--primary))]">{BOOK_SQUINT.subtitle}</span>
              </h2>
              <p className="mt-4 text-[15px] max-w-2xl text-[hsl(var(--foreground))]/85">
                Written by <Link to="/team/dr-manisha-mishra" className="font-medium hover:text-[hsl(var(--primary))]">Dr. Manisha Mishra</Link> (squint &amp; paediatric ophthalmology consultant) and edited by <Link to="/about" className="font-medium hover:text-[hsl(var(--primary))]">Dr. Shantanu Kumar Gupta</Link> — a plain-language guide that helps patients and parents replace WhatsApp myths with clear, evidence-based understanding of squint.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to={`/book/${BOOK_SQUINT.slug}`} className="btn-primary" data-testid="home-book-details-btn">
                  Read about the book
                </Link>
                <a
                  href={BOOK_SQUINT.amazonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost"
                  data-testid="home-book-amazon-btn"
                >
                  View on Amazon ↗
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AS FEATURED IN — real print press mentions, rendered as pill
          links below the trust strip. Hidden when there are no
          mentions so the section never renders empty. */}
      {PRESS_MENTIONS.length > 0 && (
        <section className="py-10 border-b border-border" data-testid="home-press-band">
          <div className="container-page flex flex-wrap items-center justify-center gap-3">
            <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mr-2">
              As featured in
            </div>
            {PRESS_MENTIONS.map((m) => (
              <NewspaperFeature key={m.id} mention={m} variant="band" />
            ))}
          </div>
        </section>
      )}

      {/* SYMPTOM CHECKER — hero-level signature band */}
      <section className="py-16 sm:py-24" data-testid="home-symptom-checker-highlight">
        <div className="container-page">
          <div className="relative overflow-hidden rounded-3xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary))] via-[hsl(var(--primary))] to-[#0a1e33] text-white px-6 sm:px-12 lg:px-16 py-14 sm:py-20 grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-10 items-center">
            {/* Ambient accent glow */}
            <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[hsl(var(--accent))]/20 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-[hsl(var(--accent))]/10 blur-3xl pointer-events-none" aria-hidden="true" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-6">
                <Sparkles size={13} strokeWidth={1.8} /> New · India's First of its kind
              </div>
              <h2 className="font-serif text-[38px] sm:text-5xl lg:text-[56px] leading-[1.04] text-balance text-white">
                Not sure what&apos;s wrong with your eyes?
                <br />
                <span className="italic text-[hsl(var(--accent))]">Try one of India&apos;s first doctor-supervised AI Eye Symptom Checkers.</span>
              </h2>
              <p className="mt-7 text-[15px] sm:text-base leading-relaxed text-white/75 max-w-xl">
                An educational triage tool designed by <span className="text-white font-medium">Dr. Shantanu Kumar Gupta &amp; Dr. Manisha Mishra</span> — MAMC / New Delhi trained ophthalmologists. Understand how urgent your symptoms are in under 60 seconds.
              </p>
              <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-white/80">
                <li className="inline-flex items-center gap-1.5"><Check size={13} className="text-[hsl(var(--accent))]" strokeWidth={2.4}/> Voice input</li>
                <li className="inline-flex items-center gap-1.5"><Check size={13} className="text-[hsl(var(--accent))]" strokeWidth={2.4}/> Photo attach</li>
                <li className="inline-flex items-center gap-1.5"><Check size={13} className="text-[hsl(var(--accent))]" strokeWidth={2.4}/> Hindi mode</li>
                <li className="inline-flex items-center gap-1.5"><Check size={13} className="text-[hsl(var(--accent))]" strokeWidth={2.4}/> Doctor-designed</li>
              </ul>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/symptom-checker"
                  data-testid="home-symptom-checker-cta"
                  className="inline-flex items-center gap-2 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-[hsl(var(--primary))] font-medium px-6 py-3.5 rounded-full text-[14px] uppercase tracking-[0.14em] transition-colors shadow-[0_20px_40px_-15px_rgba(0,0,0,0.4)]"
                >
                  <Sparkles size={16} strokeWidth={1.9} /> Start the Symptom Checker
                </Link>
                <Link
                  to="/symptoms/red-eye"
                  data-testid="home-symptom-guides-cta"
                  className="inline-flex items-center gap-2 border border-white/25 text-white hover:bg-white/10 px-6 py-3.5 rounded-full text-[14px] uppercase tracking-[0.14em] transition-colors"
                >
                  Browse symptom guides
                </Link>
              </div>
              <p className="mt-6 text-[11.5px] leading-relaxed text-white/55 max-w-2xl">
                Educational only — this tool cannot examine your eye and is not a replacement for a physical consultation with an ophthalmologist.
                <span className="block mt-1 text-[10.5px] uppercase tracking-[0.18em] text-white/40">
                  Concept by Dr. Shantanu Kumar Gupta &amp; Dr. Manisha Mishra · Developed with AI
                </span>
              </p>
            </div>

            {/* Sample-result panel */}
            <div className="relative hidden lg:block">
              <div className="rounded-2xl bg-white/95 backdrop-blur-sm p-6 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.6)] rotate-[1.5deg] hover:rotate-0 transition-transform duration-500">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] mb-3">Sample result</div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                  <span className="text-[11px] uppercase tracking-[0.22em] text-orange-500 font-medium">Urgent</span>
                </div>
                <p className="font-serif text-lg leading-snug text-[hsl(var(--primary))]">
                  Blurred vision in one eye — see an ophthalmologist within 24 hours.
                </p>
                <div className="mt-5 space-y-2 text-[12px] text-[hsl(var(--primary))]/75">
                  <div className="flex items-start gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-orange-500" /> Possible: vitreous haemorrhage, macular oedema</div>
                  <div className="flex items-start gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-orange-500" /> Diabetic retina causes are top of the list</div>
                  <div className="flex items-start gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-orange-500" /> OCT + dilated exam within 24 hours</div>
                </div>
                <div className="mt-5 pt-4 border-t border-[hsl(var(--primary))]/10 text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--primary))]/50">
                  Anonymous · under 60 seconds
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERTISE BENTO */}
      <section className="py-24 sm:py-32" data-testid="home-expertise-section">
        <div className="container-page">
          <SectionHeader
            overline="Areas of Expertise"
            title="Every eye deserves world-class care."
            subtitle="From premium cataract surgery to advanced retina diagnostics, every service is delivered by Dr. Shantanu with the latest evidence-based protocols and modern technology."
            testid="expertise-heading"
          />

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="expertise-grid">
            {expertise.map((e, idx) => {
              const Icon = iconMap[e.icon] || Eye;
              const isBig = idx === 0;
              return (
                <Link
                  to={`/expertise/${e.slug}`}
                  key={e.slug}
                  data-testid={`expertise-card-${e.slug}`}
                  className={`group card-elegant p-8 ${isBig ? "md:col-span-2 lg:row-span-2 bg-[hsl(var(--primary))] text-white hover:!border-[hsl(var(--accent))]" : ""}`}
                >
                  <Icon
                    size={isBig ? 40 : 30}
                    strokeWidth={1.25}
                    className={isBig ? "text-[hsl(var(--accent))]" : "text-[hsl(var(--primary))]"}
                  />
                  <h3 className={`font-serif mt-6 ${isBig ? "text-3xl sm:text-4xl" : "text-2xl"} leading-tight`}>
                    {e.title}
                  </h3>
                  <p className={`mt-4 leading-relaxed text-sm ${isBig ? "text-white/85" : "text-[hsl(var(--muted-foreground))]"}`}>
                    {e.tagline}
                  </p>
                  <div className={`mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] ${isBig ? "text-[hsl(var(--accent))]" : "text-[hsl(var(--primary))]"}`}>
                    Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* TECHNOLOGY */}
      <section className="py-24 sm:py-32 bg-[hsl(var(--secondary))]/40 border-y border-border" data-testid="home-tech-section">
        <div className="container-page grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <SafeImage
              src={CLINIC_PHOTOS.operatingTheatre}
              alt="Operating theatre at Shantanu Netralaya"
              label="Operating Theatre"
              className="w-full h-[500px] rounded-2xl"
            />
            <div className="hidden md:block absolute -bottom-8 -right-8 w-56 bg-white dark:bg-card p-6 rounded-2xl shadow-xl border border-border">
              <div className="overline">Featured tech</div>
              <div className="font-serif text-xl mt-1">Precision OT</div>
              <div className="text-xs text-muted-foreground mt-1">German-engineered surgical microscopes for micron-level precision.</div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <SectionHeader
              overline="Technology"
              title="Modern instruments. Timeless attention."
              subtitle="Every diagnosis at Shantanu Netralaya is backed by the latest generation of ophthalmic imaging and surgical platforms."
              testid="tech-heading"
            />
            <ul className="mt-8 space-y-4">
              {[
                "Torsional Phacoemulsification (MICS)",
                "Premium monofocal, toric, EDOF & trifocal IOLs",
                "Spectral-Domain OCT & OCT-Angiography",
                "Ultra-wide-field Fundus Photography",
                "Retinal Laser (green, PRP)",
                "Automated Perimetry & Corneal Topography",
              ].map((t) => (
                <li key={t} className="flex gap-3 items-start">
                  <CheckCircle2 size={18} strokeWidth={1.5} className="text-[hsl(var(--accent))] mt-1" />
                  <span className="text-[15px] text-[hsl(var(--foreground))]/85">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 sm:py-32" data-testid="home-testimonials-section">
        <div className="container-page">
          <SectionHeader
            overline="Patient Voices"
            title="Trusted by families across eastern UP."
            testid="testimonials-heading"
          />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t) => (
              <div key={t.name} className="card-elegant p-8" data-testid={`testimonial-${t.name.replace(/\s/g, "-").toLowerCase()}`}>
                <Quote size={28} strokeWidth={1.25} className="text-[hsl(var(--accent))]" />
                <p className="mt-5 text-[15px] leading-relaxed text-[hsl(var(--foreground))]/85">"{t.quote}"</p>
                <div className="mt-6 pt-6 border-t border-border/60">
                  <div className="font-serif text-lg">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.procedure} · {t.location}</div>
                  <div className="mt-2 flex gap-0.5 text-[hsl(var(--accent))]">
                    {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={12} fill="currentColor" strokeWidth={0} />)}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/testimonials" className="text-sm uppercase tracking-[0.22em] text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))] inline-flex items-center gap-2">
              Read all reviews <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* KASHI CONTEXT STRIP — visually anchor Varanasi (clinic → ghat → boat → Sarnath) */}
      <section className="py-20 sm:py-24 border-t border-border" data-testid="home-kashi-strip">
        <div className="container-page">
          <div className="grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-10 items-end">
            <div>
              <div className="overline">Rooted in Kashi</div>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight text-balance text-[hsl(var(--foreground))]">
                A modern eye practice in one of the oldest cities on earth.
              </h2>
            </div>
            <p className="prose-elegant lg:text-right">
              Shantanu Netralaya sits in Durgakund, a quiet neighbourhood just south of the ghats of the Ganga.
              Kashi — Varanasi — has been a place of learning and healing for millennia, and our clinic is
              proud to carry that quiet tradition into modern ophthalmology.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="kashi-strip-grid">
            {[
              {
                src: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/55a2lhi6_887d09f6-aac0-453c-bc07-62fb61dcc9cc.jpg",
                caption: "Shantanu Netralaya · Durgakund",
                alt: "Shantanu Netralaya Eye Hospital exterior at Durgakund, Varanasi",
                testid: "kashi-tile-clinic",
              },
              {
                src: "https://images.unsplash.com/photo-1752733904669-8a2af6d349f3?w=1200&q=70&auto=format&fit=crop",
                caption: "Ghats of the Ganga",
                alt: "Riverfront ghats along the Ganga at Varanasi",
                testid: "kashi-tile-ghat",
                credit: "Photo · Varsha Prakash, Unsplash",
              },
              {
                src: "https://images.unsplash.com/photo-1646288744226-a2e3a06bfb10?w=1200&q=70&auto=format&fit=crop",
                caption: "Boats on the Ganga",
                alt: "A wooden boat on the Ganga near the Varanasi ghats",
                testid: "kashi-tile-boat",
                credit: "Photo · Aman Gupta, Unsplash",
              },
              {
                src: "https://images.unsplash.com/photo-1757941898945-776cb3673f91?w=1200&q=70&auto=format&fit=crop",
                caption: "Dhamek Stupa · Sarnath",
                alt: "Dhamek Stupa at the Sarnath archaeological site near Varanasi",
                testid: "kashi-tile-sarnath",
                credit: "Photo · Rohit Dey, Unsplash",
              },
            ].map((tile) => (
              <figure
                key={tile.testid}
                className="group relative overflow-hidden rounded-xl border border-border aspect-[3/4]"
                data-testid={tile.testid}
              >
                <img
                  src={tile.src}
                  alt={tile.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition duration-700 ease-out group-hover:scale-[1.04]"
                />
                <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 via-black/30 to-transparent text-white">
                  <div className="text-[10.5px] uppercase tracking-[0.2em]">{tile.caption}</div>
                  {tile.credit && (
                    <div className="mt-1 text-[9.5px] uppercase tracking-[0.18em] text-white/55 opacity-0 group-hover:opacity-100 transition-opacity">
                      {tile.credit}
                    </div>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CLINIC GALLERY */}
      <section className="py-24 sm:py-32 border-t border-border" data-testid="home-gallery-section">
        <div className="container-page">
          <SectionHeader
            overline="Inside Shantanu Netralaya"
            title="A calm, modern space built around your comfort."
            subtitle="Where I consult and operate — designed for warmth, cleanliness and a stress-free patient experience."
            testid="gallery-heading"
          />
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="clinic-gallery">
            {CLINIC_GALLERY.map((g, i) => (
              <figure
                key={i}
                className={`group relative overflow-hidden rounded-xl border border-border ${g.tall ? "col-span-2 row-span-2 aspect-square lg:aspect-auto lg:h-full" : "aspect-[4/3]"}`}
                data-testid={`gallery-item-${i}`}
              >
                <SafeImage src={g.src} alt={g.caption} label={g.caption} className="w-full h-full" />
                <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-white text-[11px] uppercase tracking-[0.16em] opacity-0 group-hover:opacity-100 transition">
                  {g.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHTS / BLOG */}
      <section className="py-24 sm:py-32 border-t border-border bg-[hsl(var(--secondary))]/40" data-testid="home-insights-section">
        <div className="container-page">
          <SectionHeader
            overline="Insights"
            title="From the desk of Dr. Shantanu."
            subtitle="Deep-dive articles, patient stories and evidence-based guides — updated regularly."
            testid="insights-heading"
          />
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {featuredBlog.slice(0, 3).map((b) => (
              <Link
                to={`/blog/${b.slug}`}
                key={b.slug}
                data-testid={`insight-card-${b.slug}`}
                className="group block"
              >
                <div className="relative overflow-hidden rounded-xl aspect-[4/3]">
                  <SafeImage
                    src={b.cover_image}
                    alt={b.title}
                    label={b.title}
                    className="w-full h-full rounded-xl"
                  />
                </div>
                <div className="mt-5">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">{b.category} · {b.reading_time} min</div>
                  <h3 className="font-serif text-2xl mt-2 group-hover:text-[hsl(var(--primary))] transition-colors leading-snug">{b.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{b.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                    Read article <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-24 sm:py-28 bg-[hsl(var(--navy))] text-white relative overflow-hidden" data-testid="home-cta-section">
        <div className="absolute inset-0 -z-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }} />
        </div>
        <div className="container-page relative text-center max-w-3xl mx-auto">
          <div className="overline text-[hsl(var(--accent))] mb-4">Ready when you are</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-balance">
            Take the next step toward
            <span className="italic text-[hsl(var(--accent))]"> clearer vision.</span>
          </h2>
          <p className="mt-6 text-white/70 max-w-xl mx-auto">
            Message us on WhatsApp for the fastest response, or call the clinic directly.
            Same-day appointments are frequently available.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <a
              href={waLink()}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="btn-gold"
              data-testid="cta-whatsapp-btn"
            >
              <MessageCircle size={16} /> WhatsApp {CLINIC.whatsapp}
            </a>
            <a
              href={`tel:${CLINIC.phoneRaw}`}
              className="btn-ghost !border-white/40 !text-white hover:!bg-white/10"
              data-testid="cta-call-btn"
            >
              Call {CLINIC.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
